<script setup lang="ts">
import { Bot, CircleCheck, Clock, Sparkles } from '@lucide/vue';
import SectionHeading from '@/components/marketing/shared/SectionHeading.vue';

const capabilities = [
    'Choose a goal, such as growing followers or promoting a product',
    'Get a plan and drafts instead of a blank page',
    'Correct it once, and it remembers for next time',
    'See every action it took, and why',
    'Approve sensitive actions before anything is published',
];

const messages = [
    {
        from: 'user',
        text: 'Create five Instagram posts promoting our new product.',
    },
    {
        from: 'agent',
        text: 'Here are five drafts with captions and hashtags. Two need your decision on pricing wording.',
    },
];

const activity = [
    { icon: Sparkles, label: 'Prepared 5 drafts' },
    { icon: Clock, label: 'Waiting for approval' },
];
</script>

<template>
    <section id="ai-assistant" class="scroll-mt-24 py-16 sm:py-20">
        <div class="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeading
                align="left"
                eyebrow="AI assistant"
                title="An assistant that already knows your business."
                description="Talk to it like a colleague. It plans, drafts and schedules, then shows you exactly what it did."
            />

            <div class="mt-12 grid gap-10 lg:grid-cols-2 lg:items-center">
                <ul class="grid gap-3">
                    <li
                        v-for="item in capabilities"
                        :key="item"
                        class="flex items-start gap-3 text-sm sm:text-base"
                    >
                        <CircleCheck
                            class="text-secondary mt-0.5 size-4 shrink-0"
                            aria-hidden="true"
                        />
                        {{ item }}
                    </li>
                </ul>

                <div
                    class="bg-card border-border rounded-2xl border p-5 shadow-sm"
                >
                    <div class="mb-4 flex items-center gap-2">
                        <span
                            class="bg-primary/10 text-primary inline-flex size-8 items-center justify-center rounded-lg"
                        >
                            <Bot class="size-4" aria-hidden="true" />
                        </span>
                        <span class="text-sm font-medium">
                            Your assistant
                        </span>
                        <span
                            class="bg-secondary/10 text-muted-foreground ml-auto rounded-full px-2 py-0.5 text-xs"
                        >
                            Preview
                        </span>
                    </div>

                    <div class="grid gap-3">
                        <div
                            v-for="message in messages"
                            :key="message.text"
                            :class="[
                                'max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
                                message.from === 'user'
                                    ? 'bg-muted ml-auto'
                                    : 'border-border bg-background border',
                            ]"
                        >
                            {{ message.text }}
                        </div>
                    </div>

                    <div
                        class="border-border mt-4 flex flex-wrap items-center gap-2 border-t pt-4"
                    >
                        <span
                            v-for="item in activity"
                            :key="item.label"
                            class="bg-muted text-muted-foreground inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs"
                        >
                            <component
                                :is="item.icon"
                                class="size-3.5"
                                aria-hidden="true"
                            />
                            {{ item.label }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>
